import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, MapPin, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useLang } from "@/i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDay = (d) => new Date(`${d}T00:00:00`).getDate();
const fmtMonth = (d) => new Date(`${d}T00:00:00`).toLocaleString("en-GB", { month: "short" }).toUpperCase();

export default function Events() {
  const { t } = useLang();
  const [events, setEvents] = useState(null);
  const types = t("events.types");

  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  return (
    <section id="events" data-testid="events-section" className="px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto max-w-5xl">
        <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>{t("events.eyebrow")}</Eyebrow>
            <h2 className="font-display mt-6 text-3xl font-light tracking-tight sm:text-4xl lg:text-5xl">
              {t("events.h1")}
              <span className="italic" style={{ color: "var(--accent-gold)" }}>{t("events.h2")}</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {t("events.sub")}
          </p>
        </Reveal>

        <div>
          {events && events.length === 0 && (
            <p data-testid="events-empty" className="py-12 text-center text-sm" style={{ color: "var(--text-muted)" }}>
              {t("events.empty")}
            </p>
          )}
          {(events || []).map((e, i) => (
            <Reveal key={e.id} delay={i * 0.06}>
              <article
                data-testid={`event-card-${i + 1}`}
                className="group flex flex-col gap-5 border-t py-8 transition-colors sm:flex-row sm:items-start sm:gap-10"
                style={{ borderColor: "var(--border-delicate)" }}
              >
                <div className="flex items-center gap-4 sm:w-28 sm:shrink-0 sm:flex-col sm:items-start">
                  <span className="font-display text-5xl font-light leading-none sm:text-6xl" style={{ color: "var(--accent-gold)" }}>
                    {fmtDay(e.date)}
                  </span>
                  <span className="font-mono-alt text-xs uppercase tracking-[0.3em]" style={{ color: "var(--text-muted)" }}>
                    {fmtMonth(e.date)}
                    {e.endDate ? ` – ${fmtDay(e.endDate)} ${fmtMonth(e.endDate)}` : ""}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display text-2xl font-normal leading-snug sm:text-3xl">{e.title}</h3>
                    <span className="rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em]"
                      style={{ backgroundColor: "var(--pill-tag-bg)", color: "var(--pill-tag-text)" }}>
                      {types[e.eventType] || e.eventType}
                    </span>
                  </div>
                  {e.location && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs" style={{ color: "var(--text-muted)" }}>
                      <MapPin size={12} /> {e.location}
                    </p>
                  )}
                  {e.description && (
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                      {e.description}
                    </p>
                  )}
                  {e.link && (
                    <a
                      data-testid={`event-join-${i + 1}`}
                      href={e.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-transform hover:scale-[1.04]"
                      style={{ backgroundColor: "var(--primary-btn-bg)", color: "var(--primary-btn-text)" }}
                    >
                      {t("events.join")} <ArrowUpRight size={13} />
                    </a>
                  )}
                </div>
                <CalendarDays size={18} className="hidden shrink-0 opacity-0 transition-opacity group-hover:opacity-60 sm:block" style={{ color: "var(--accent-gold)" }} />
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
